import { useCallback, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { ChevronsLeftRight } from "lucide-react";

export const BeforeAfterSlider = ({ before, after, altBefore, altAfter, framed = true }) => {
    const ref = useRef(null);
    const [pos, setPos] = useState(15);
    const dragging = useRef(false);
    const hintRaf = useRef(null);

    // Animazione di invito: alla prima comparsa il cursore oscilla una volta
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry.isIntersecting) return;
                observer.disconnect();
                const start = performance.now();
                const DURATION = 1800;
                const tick = (now) => {
                    if (dragging.current) return;
                    const t = Math.min(1, (now - start) / DURATION);
                    // sinusoide: 15 -> 42 -> 15
                    setPos(15 + 27 * Math.sin(t * Math.PI));
                    if (t < 1) {
                        hintRaf.current = requestAnimationFrame(tick);
                    } else {
                        setPos(15);
                    }
                };
                hintRaf.current = requestAnimationFrame(tick);
            },
            { threshold: 0.6 },
        );
        observer.observe(el);
        return () => {
            observer.disconnect();
            cancelAnimationFrame(hintRaf.current);
        };
    }, []);

    const update = useCallback((clientX) => {
        const el = ref.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const pct = ((clientX - rect.left) / rect.width) * 100;
        setPos(Math.min(96, Math.max(4, pct)));
    }, []);

    const onPointerDown = (e) => {
        dragging.current = true;
        cancelAnimationFrame(hintRaf.current);
        try {
            e.currentTarget.setPointerCapture?.(e.pointerId);
        } catch {}
        update(e.clientX);
    };
    const onPointerMove = (e) => {
        if (dragging.current) update(e.clientX);
    };
    const stop = () => {
        dragging.current = false;
    };

    const onKeyDown = (e) => {
        if (e.key === "ArrowLeft") setPos((p) => Math.max(4, p - 5));
        if (e.key === "ArrowRight") setPos((p) => Math.min(96, p + 5));
    };

    return (
        <div
            ref={ref}
            data-testid="before-after-slider"
            className={`relative aspect-[3/2] w-full touch-none select-none overflow-hidden bg-ink ${
                framed ? "border-2 border-ink shadow-print" : ""
            }`}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={stop}
            onPointerCancel={stop}
            onPointerLeave={stop}
        >
            <img
                src={after}
                alt={altAfter}
                className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                draggable="false"
            />
            <div
                className="absolute inset-0"
                style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
            >
                <img
                    src={before}
                    alt={altBefore}
                    className="pointer-events-none absolute inset-0 h-full w-full object-cover"
                    draggable="false"
                />
            </div>
            <span className="pointer-events-none absolute left-3 top-3 border-2 border-ink bg-cream px-2 py-1 text-xs font-bold uppercase tracking-widest text-ink">
                Prima
            </span>
            <span className="pointer-events-none absolute right-3 top-3 border-2 border-ink bg-royal px-2 py-1 text-xs font-bold uppercase tracking-widest text-white">
                Dopo
            </span>
            <div
                className="pointer-events-none absolute inset-y-0"
                style={{ left: `${pos}%` }}
            >
                <div className="absolute inset-y-0 -ml-px w-0.5 bg-cream" />
                <button
                    type="button"
                    data-testid="before-after-handle"
                    aria-label="Trascina il cursore per confrontare il faro prima e dopo il ripristino (usa le frecce della tastiera)"
                    role="slider"
                    aria-valuenow={Math.round(pos)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    onKeyDown={onKeyDown}
                    className="pointer-events-auto absolute top-1/2 flex h-12 w-12 -translate-x-1/2 -translate-y-1/2 items-center justify-center border-2 border-ink bg-wa text-ink"
                >
                    <ChevronsLeftRight className="h-6 w-6" aria-hidden="true" />
                </button>
            </div>
        </div>
    );
};

export const BeforeAfter = () => (
    <section id="prima-dopo" className="scroll-mt-20 border-b-2 border-ink bg-navy text-cream navy-lines">
        <div className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5 }}
                className="mx-auto max-w-3xl text-center"
            >
                <p className="mb-4 inline-block border-2 border-cream bg-navy-deep px-3 py-1 text-xs font-bold uppercase tracking-widest">
                    I risultati
                </p>
                <h2 className="headline-display text-4xl font-bold sm:text-5xl">
                    Prima e dopo, senza filtri
                </h2>
                <p className="mt-6 text-base font-medium leading-relaxed text-cream/80 md:text-lg">
                    Trascina il cursore per confrontare il faro prima e dopo il
                    trattamento. Il risultato dipende dalle condizioni iniziali:
                    per questo valutiamo sempre il tuo caso dalle foto.
                </p>
            </motion.div>
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-80px" }}
                transition={{ duration: 0.5, delay: 0.1 }}
                className="mx-auto mt-12 max-w-4xl"
            >
                <BeforeAfterSlider
                    before="/assets/lavori/golf-prima.jpg"
                    after="/assets/lavori/golf-dopo.jpg"
                    altBefore="Faro Golf completamente opaco prima del trattamento"
                    altAfter="Lo stesso faro Golf limpido e definito dopo il trattamento"
                />
                <p className="mt-3 text-center text-xs font-bold uppercase tracking-widest text-cream/60">
                    Opacità grave — restauro completo
                </p>
            </motion.div>

            <div className="mx-auto mt-14 grid max-w-6xl gap-8 lg:grid-cols-3 lg:items-start">
                {[
                    {
                        before: "/assets/lavori/citroen-prima.jpg",
                        after: "/assets/lavori/citroen-dopo.jpg",
                        altBefore: "Faro Citroën velato prima del trattamento",
                        altAfter: "Lo stesso faro Citroën trasparente dopo il trattamento",
                        caption: "Velatura superficiale — lucidatura e protezione",
                    },
                    {
                        before: "/assets/lavori/honda-prima.jpg",
                        after: "/assets/lavori/honda-dopo.jpg",
                        altBefore: "Faro Honda opaco prima del ripristino",
                        altAfter: "Lo stesso faro Honda brillante dopo il ripristino",
                        caption: "Opacità diffusa — ripristino completo",
                    },
                    {
                        before: "/assets/lavori/nera-prima.jpg",
                        after: "/assets/lavori/nera-dopo.jpg",
                        altBefore: "Faro opaco mascherato con nastro di protezione prima del ripristino",
                        altAfter: "Lo stesso faro trasparente dopo il ripristino",
                        caption: "Ossidazione estesa — ripristino completo",
                    },
                ].map((w, i) => (
                    <motion.div
                        key={w.caption}
                        initial={{ opacity: 0, y: 24 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 0.5, delay: (i % 2) * 0.08 }}
                    >
                        <BeforeAfterSlider
                            before={w.before}
                            after={w.after}
                            altBefore={w.altBefore}
                            altAfter={w.altAfter}
                        />
                        <p className="mt-3 text-center text-xs font-bold uppercase tracking-widest text-cream/60">
                            {w.caption}
                        </p>
                    </motion.div>
                ))}
            </div>
        </div>
    </section>
);
